import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/api/client';
import type {
  AdjustStockRequest,
  CreateMedicineRequest,
  RestockRequest,
  UpdateMedicineRequest,
} from '@/api/inventory';
import type { Medicine, MedicineSearchIndex } from '@/api/types';

interface MedicinesQuery {
  search?: string;
  category?: string;
  status?: string;
  page?: number;
  limit?: number;
  [key: string]: string | number | boolean | null | undefined;
}

interface MedicinesPage {
  items: Medicine[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export function useMedicines(query: MedicinesQuery = {}, enabled = true) {
  return useQuery({
    queryKey: ['medicines', query],
    queryFn: () => api.get<MedicinesPage>('/api/medicines', query),
    enabled,
  });
}

/**
 * Full catalogue for the billing screen, cached for the session and filtered
 * to sellable items client-side. The billing grid needs to search as the
 * pharmacist types — a network round trip per keystroke is too slow at a
 * counter with a queue, and the API has no "sellable" status filter (its
 * `status` query param is for the inventory screen's out/low/expiring/expired
 * tabs, which is a different question).
 *
 * Uses /api/medicines/search-index rather than a paged /api/medicines call:
 * the paged endpoint clamps `limit` to 200 server-side, so any pharmacy with
 * a larger catalogue silently couldn't find — and therefore couldn't bill —
 * its older stock. The index returns every row, slimmed to just the fields
 * this search needs.
 */
export function useBillingCatalogue() {
  return useQuery({
    queryKey: ['medicines', 'billing-catalogue'],
    queryFn: () => api.get<MedicineSearchIndex[]>('/api/medicines/search-index'),
    staleTime: 60_000,
    // Out-of-stock items can't be billed — the server rejects them
    // (INSUFFICIENT_STOCK), so keep them out of the search results rather
    // than let someone add one and get bounced.
    select: (items) => items.filter((m) => m.stock > 0),
  });
}

export function useFrequentlySold(limit = 10) {
  return useQuery({
    queryKey: ['medicines', 'frequently-sold', limit],
    queryFn: () =>
      api.get<Array<{ medicine: Medicine; score: number }>>('/api/medicines/frequently-sold', {
        limit,
      }),
    staleTime: 60_000,
  });
}

export function useCreateMedicine() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: CreateMedicineRequest) => api.post<Medicine>('/api/medicines', request),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['medicines'] }),
  });
}

export function useUpdateMedicine() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, request }: { id: string; request: UpdateMedicineRequest }) =>
      api.put<Medicine>(`/api/medicines/${id}`, request),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['medicines'] }),
  });
}

export function useDeleteMedicine() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/medicines/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['medicines'] }),
  });
}

/**
 * PUT /{id}/stock accepts either an absolute `stock` figure (what this app's
 * dialog still sends) or a signed `delta` — the server prefers `delta` when
 * both are present, since an absolute figure computed from a stale list can
 * silently overwrite a sale that landed in between. `stock` stays supported
 * for exactly the case this hook is used for today; a future caller wanting
 * the race-safe path can pass `delta`/`expectedStock` instead without any
 * change here.
 */
export function useAdjustStock() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, request }: { id: string; request: AdjustStockRequest }) =>
      api.put<Medicine>(`/api/medicines/${id}/stock`, request),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['medicines'] }),
  });
}

export function useRestockMedicine() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, request }: { id: string; request: RestockRequest }) =>
      api.post<Medicine>(`/api/medicines/${id}/restock`, request),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['medicines'] }),
  });
}

// ── Bulk import ──────────────────────────────────────────────────────────
// Types live here (not api/inventory.ts or api/types.ts) because
// BulkImportDialog.tsx has always imported them from this hooks module —
// keeping that import path intact rather than also touching the dialog.

/** One parsed spreadsheet row, before the server validates it. */
export interface ImportMedicineRow {
  name?: string;
  shortName?: string;
  genericName?: string;
  composition?: string;
  category?: string;
  unit?: string;
  stock?: number;
  minStock?: number;
  price?: number;
  costPrice?: number;
  gstRate?: number;
  expiryDate?: string;
  batchNo?: string;
  location?: string;
}

export interface ImportError {
  /** 1-based spreadsheet row, header included, so it matches what the user sees in Excel. */
  row: number;
  name: string;
  error: string;
}

export interface ImportResult {
  created: number;
  skipped: number;
  errors: ImportError[];
}

export function useImportMedicines() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (rows: ImportMedicineRow[]) =>
      api.post<ImportResult>('/api/medicines/import', { medicines: rows }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['medicines'] }),
  });
}
